/**
 * Subscription flow management hook
 * Handles DocuSign integration and subscription signing
 *
 * Uses shared useDocumentState for document state management
 */

import { useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { useSubcriptionDocuSign } from "../../../hooks/useDocuSign";
import usePostSubscriptionAgreementSigned from "../../../hooks/usePostSubscriptionAgreementSigned";
import useSignedDocument from "../../../hooks/useSignedDocument";
import { WEB_DOMAIN } from "../../../constants/domain";
import {
  getBaseUrl,
  Microservice,
} from "@funded-here-interface/common/src/constant/baseUrl";
import {
  MESSAGES,
  useDocumentState,
} from "../../../components/DocumentSigning";
import { clearSubscriptionSession } from "../utils/sessionStorage";
import {
  InvestmentOpportunity,
  SubscriptionInitResponse,
  SubscriptionResponse,
} from "../StructureNote.types";
import useGetDocSignatureStatusUpdate from "../../../hooks/useGetDocumentSignatureStatusUpdate";
/**
 * Hook to manage the subscription agreement signing flow
 * Uses shared useDocumentState for document state management
 *
 * @param token - Authentication token
 * @param regtankLivenessFeatureFlag - Feature flag for liveness verification
 * @param documentState - Document state from parent (optional, creates own if not provided)
 * @returns Subscription state and flow functions
 */
export const useSubscriptionFlow = (
  token: string,
  regtankLivenessFeatureFlag?: boolean,
  documentState?: ReturnType<typeof useDocumentState>,
) => {
  // Use shared document state hook (or use provided one from parent)
  const defaultDocumentState = useDocumentState();
  const activeDocumentState = documentState || defaultDocumentState;

  // Subscription-specific state
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [offerSigned, setOfferSigned] = useState(false);

  //Get agreement URL hook
  const docuSignMutation = useSubcriptionDocuSign();
  const docuSignMutateRef = useRef(docuSignMutation.mutate);
  docuSignMutateRef.current = docuSignMutation.mutate;

  const subscriptionAgreementMutation = usePostSubscriptionAgreementSigned();
  const subscriptionMutateRef = useRef(subscriptionAgreementMutation.mutate);
  subscriptionMutateRef.current = subscriptionAgreementMutation.mutate;

  const signedDocumentMutation = useSignedDocument();
  const signedDocMutateRef = useRef(signedDocumentMutation.mutate);
  signedDocMutateRef.current = signedDocumentMutation.mutate;

  const documentSignatureUpdateMutation = useGetDocSignatureStatusUpdate();
  const docSigUpdateMutateRef = useRef(documentSignatureUpdateMutation.mutate);
  docSigUpdateMutateRef.current = documentSignatureUpdateMutation.mutate;

  /**
   * Initiate document signing flow
   */
  const initiateSubscription = useCallback(
    async (
      selectedData: InvestmentOpportunity,
      investorSubscribeAmount: string,
    ): Promise<SubscriptionInitResponse | void> => {
      if (!selectedData) {
        toast.error("No investment opportunity selected.");
        return;
      }

      toast.info(MESSAGES.LOADING_DOC);
      setIsOverlayVisible(true);

      return new Promise<SubscriptionInitResponse>((resolve, reject) => {
        docuSignMutateRef.current(
          {
            token,
            structuredNoteId: selectedData.id,
            investedAmount: investorSubscribeAmount,
            webDomain: WEB_DOMAIN,
            redirectUrl: `${getBaseUrl(Microservice.DOCUMENT_MANAGEMENT)}/document-management/docusign/webhook`,
            regtankLivenessFeatureFlag: regtankLivenessFeatureFlag || undefined,
            displayStructuredNoteId: String(
              selectedData.structuredNoteDisplayId ?? "",
            ),
          },
          {
            onSuccess: (data) => {
              activeDocumentState.updateDocument(
                data.url,
                data.documentId,
                data.completedAt,
              );
              resolve(data);
            },
            onError: (e) => {
              toast.error((e as Error).message);
              clearSubscriptionSession();
              setIsOverlayVisible(false);
              reject(e);
              return;
            },
          },
        );
      });
    },
    [token, regtankLivenessFeatureFlag, activeDocumentState],
  );

  /**
   * Complete subscription after signing
   * Returns subscription response data for caller to handle
   * Original logic from handleSubscriptionAgreementSigned (lines 230-270)
   */
  const completeSubscription = useCallback(
    async (
      selectedData: InvestmentOpportunity,
      investorSubscribeAmount: string,
    ): Promise<SubscriptionResponse> => {
      if (!selectedData) {
        toast.error("No investment opportunity selected.");
        throw new Error("No investment opportunity selected.");
      }

      return new Promise<SubscriptionResponse>((resolve, reject) => {
        subscriptionMutateRef.current(
          {
            token,
            structuredNoteId: selectedData.id,
            investorSubscribedAmount: investorSubscribeAmount,
          },
          {
            onSuccess: (data) => {
              // Only show success toast for full subscriptions
              // Let caller handle partial subscription UI
              if (!data.isPartialSubscription) {
                toast.success(MESSAGES.SIGNING_SUCCESS);
              }
              // Clear session storage (original lines 245-251)
              clearSubscriptionSession();
              setIsOverlayVisible(false);
              setOfferSigned(true);
              resolve(data);
            },
            onError: (e) => {
              // Clear session storage (original lines 257-263)
              clearSubscriptionSession();
              setIsOverlayVisible(false);
              reject(e);
            },
          },
        );
      });
    },
    [token],
  );

  /**
   * Fetch signed document after signing
   */
  const fetchSignedDocument = useCallback(() => {
    activeDocumentState.clearUrl();
    signedDocMutateRef.current(
      { documentId: activeDocumentState.documentId, token },
      {
        onSuccess: (data) => {
          activeDocumentState.updateDocument(
            data.url,
            data.documentId,
            data.completedAt,
          );
          activeDocumentState.incrementViewerKey();
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /**
   * Fetch the document signature update and display the document when regtankLivenessFeatureFlag is on
   */

  const fetchDocumentSignatureUpdate = useCallback(
    (documentId: number): Promise<void> => {
      return new Promise<void>((resolve, reject) => {
        if (documentId === 0) {
          resolve();
          return;
        }

        // clear old URL
        activeDocumentState.clearUrl();

        docSigUpdateMutateRef.current(
          { token, documentId },
          {
            onSuccess: (data) => {
              toast.success(MESSAGES.SIGNING_SUCCESS);
              clearSubscriptionSession();
              setIsOverlayVisible(false);
              setOfferSigned(true);
              activeDocumentState.updateDocument(
                data.url,
                data.documentId,
                data.completedAt,
              );
              activeDocumentState.incrementViewerKey();
              resolve();
            },
            onError: (e) => {
              toast.error((e as Error).message);
              clearSubscriptionSession();
              setIsOverlayVisible(false);
              reject(e);
            },
          },
        );
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token],
  );

  return {
    // Document state from shared hook
    realUrl: activeDocumentState.realUrl,
    documentId: activeDocumentState.documentId,
    completedAt: activeDocumentState.completedAt,
    viewerKey: activeDocumentState.viewerKey,

    // Setters for session restore compatibility
    setRealUrl: (url: string) =>
      activeDocumentState.updateDocument(
        url,
        activeDocumentState.documentId,
        activeDocumentState.completedAt,
      ),
    setDocumentId: (id: number) =>
      activeDocumentState.updateDocument(
        activeDocumentState.realUrl,
        id,
        activeDocumentState.completedAt,
      ),

    // Subscription-specific state
    isOverlayVisible,
    offerSigned,

    // Actions
    initiateSubscription,
    completeSubscription,
    fetchSignedDocument,
    fetchDocumentSignatureUpdate,
    incrementViewerKey: activeDocumentState.incrementViewerKey,

    // Loading state
    isLoading:
      docuSignMutation.isLoading || subscriptionAgreementMutation.isLoading,
  };
};
